import type { BackendApi } from '../backend/client'
import type { DependentRow, ProfileRow, SettingsRow } from '../types/supabase'

/**
 * The account-shaped records -- profile, settings, dependents, push tokens,
 * OCR feedback -- which the web app wrote straight through PostgREST until
 * Stage 2 Step 4 of docs/plans/native-app-sdk-seams.md. Their member RLS write
 * policies are gone, so these commands are the only member write path now.
 *
 * Every one of them takes only the fields the member is changing: the row id
 * comes from the JWT for the singular records (there is exactly one profile
 * and one settings row per user), so a caller cannot name someone else's.
 */

/** Any subset of the member-writable profile columns. */
export type ProfileUpdate = Partial<
  Pick<
    ProfileRow,
    | 'full_name'
    | 'address'
    | 'phone'
    | 'phone_type'
    | 'date_of_birth'
    | 'avatar_url'
    | 'signature_data'
    | 'ssn_last_4'
    | 'alternate_emails'
    | 'onboarding_step_completed_at'
    | 'account_deletion_requested_at'
  >
> & {
  /**
   * Narrower than `ProfileRow['onboarding_steps_completed']`, which is
   * `string[] | null`: the column is nullable and stays so, but null is not a
   * value a caller may *send*. The server has no cast on this column and
   * encodes the array itself, and "no steps yet" is already expressed by not
   * sending the field. See ProfileController and the note on Profile's casts.
   */
  onboarding_steps_completed?: string[]
}

/**
 * Updates the caller's own profile, creating the row if they somehow have
 * none. `updated_at` is stamped server-side -- do not send one; the browser
 * clock is exactly what the sync engine's cursor design distrusts.
 */
export async function updateMyProfile(api: BackendApi, update: ProfileUpdate): Promise<ProfileRow> {
  const response = await api.updateMyProfile(update)
  return response.profile as ProfileRow
}

/** Any subset of the member-writable settings columns. */
export type SettingsUpdate = Partial<
  Pick<
    SettingsRow,
    | 'textract_opt_in'
    | 'share_chm_submission_warning_dismissed'
    | 'notification_prefs_email'
    | 'notification_prefs_push'
    | 'conversation_data_opt_out'
  >
>

/**
 * Updates the caller's settings, creating the row on first touch. That is why
 * there is no separate `getOrCreateSettings` command and no insert-or-update
 * branch on the client: whether the row exists is the server's business.
 */
export async function updateMySettings(api: BackendApi, update: SettingsUpdate): Promise<SettingsRow> {
  const response = await api.updateMySettings(update)
  return response.settings as SettingsRow
}

export interface SaveDependentParams {
  membershipId: string
  /** Pass to restore and update a previously removed dependent. */
  id?: string
  fullName: string
  dateOfBirth: string
  relationship?: string | null
  authorizedRepresentativeName?: string | null
  authorizedRepresentativeRelationship?: string | null
  signatureData?: string | null
  ssnLast4?: string | null
}

/**
 * Adds a dependent, or restores one that was removed. The caller must be an
 * admin of the membership, which is what the dropped RLS policy required.
 */
export async function saveDependent(api: BackendApi, params: SaveDependentParams): Promise<DependentRow> {
  const response = await api.saveDependent({
    id: params.id,
    membership_id: params.membershipId,
    full_name: params.fullName,
    date_of_birth: params.dateOfBirth,
    relationship: params.relationship,
    authorized_representative_name: params.authorizedRepresentativeName,
    authorized_representative_relationship: params.authorizedRepresentativeRelationship,
    signature_data: params.signatureData,
    ssn_last_4: params.ssnLast4,
  })
  return response.dependent as DependentRow
}

/**
 * Any subset of the member-writable dependent columns.
 *
 * `relationship` stays a string here. The column is free text, and the add
 * sheet's child/spouse pills are not the only values a row can already hold.
 * Narrowing the update to those two would rename anything else on save.
 */
export type DependentUpdate = Omit<
  Partial<
    Pick<
      DependentRow,
      | 'full_name'
      | 'date_of_birth'
      | 'relationship'
      | 'authorized_representative_name'
      | 'authorized_representative_relationship'
      | 'signature_data'
      | 'ssn_last_4'
    >
  >,
  'relationship'
> & {
  relationship?: string | null
}

export async function updateDependent(
  api: BackendApi,
  dependentId: string,
  update: DependentUpdate,
): Promise<DependentRow> {
  const response = await api.updateDependent(dependentId, update)
  return response.dependent as DependentRow
}

/**
 * Soft-deletes a dependent. The row stays readable so it can be restored
 * through {@link saveDependent} with the same id.
 */
export async function removeDependent(api: BackendApi, dependentId: string): Promise<DependentRow> {
  const response = await api.deleteDependent(dependentId)
  return response.dependent as DependentRow
}

/**
 * Registers this device's push token, deactivating the caller's active tokens
 * of the other family (Expo vs FCM) server-side. Idempotent.
 *
 * Push otherwise leaves the SDK entirely -- the host owns permission,
 * delivery and the badge -- so this is the one push operation here.
 */
export function registerPushToken(api: BackendApi, token: string) {
  return api.registerPushToken({ token })
}

export interface TextractFeedbackParams {
  membershipId: string
  blobKey: string
  expenseId: string
  parsedTextractData?: Record<string, unknown> | null
}

/**
 * Records opt-in OCR feedback for a receipt. The blob and the expense must
 * both belong to the named membership; `created_by_user_id` comes from the JWT.
 */
export function submitTextractFeedback(api: BackendApi, params: TextractFeedbackParams) {
  return api.submitTextractFeedback({
    membership_id: params.membershipId,
    blob_key: params.blobKey,
    expense_id: params.expenseId,
    parsed_textract_data: params.parsedTextractData ?? undefined,
  })
}
